import {
  HEADER_FILL,
  NA_FILL,
  TITLE_FONT,
  VALUE_FILL,
  hideRowsThrough,
} from "./detailSheetCommon.js";
import {
  COL_D,
  COL_E,
  COL_F,
  LINKED_FILL,
  borderFromSpecs,
  getFyLabels,
  internalSheetLink,
  parseBorderSpec,
  safeText,
  setCell,
  setGrey,
} from "./principle6SheetHelpers.js";
import { readLocationFY } from "./principle8SheetHelpers.js";

const DETAILS_OF_SIA_SHEET = "Details of SIA";
const PROJECT_FOR_REHABILITATION_SHEET = "Project for rehabilitation";
const ACTION_TO_MITIGATE_NEGATIVE_SIA_SHEET = "Action to mitigate negative SIA";
const CSR_IN_ASPIRATIONAL_DISTRICTS_SHEET = "CSR in aspirational districts";
const DETAILS_OF_BENEFITS_FROM_IP_SHEET = "Details of the benefits from IP";
const DETAILS_OF_CORRECTIVE_ACTION_IP_SHEET = "Details of Corrective Action IP";
const DETAILS_OF_CSR_PROJECT_SHEET = "Details of CSR Project";
const DETAIL_CELL = "D8";

const PRINCIPLE_TITLE =
  "PRINCIPLE 8 Businesses should promote inclusive growth and equitable development";

const SECTION_FONT = { name: "Calibri", size: 12, bold: true };

const SUPPLIER_ROWS = [
  ["Directly sourced from MSMEs/ small producers", "smallProducers"],
  [
    "Sourced directly from within the district and neighbouring districts",
    "neighbouringDistricts",
  ],
];

const JOB_LOCATIONS = [
  {
    key: "rural",
    band: "1. Rural",
    bandRow: 18,
    wageRow: 19,
    totalRow: 20,
    pctRow: 21,
    pctLabel: "iii) % of Job creation in Rural areas",
  },
  {
    key: "semiUrban",
    band: "2. Semi-urban",
    bandRow: 22,
    wageRow: 23,
    totalRow: 24,
    pctRow: 25,
    pctLabel: "iii) % of Job creation in Semi-Urban areas",
  },
  {
    key: "urban",
    band: "3. Urban",
    bandRow: 26,
    wageRow: 27,
    totalRow: 28,
    pctRow: 29,
    pctLabel: "iii) % of Job creation in Urban areas",
  },
  {
    key: "metropolitan",
    band: "4. Metropolitan",
    bandRow: 30,
    wageRow: 31,
    totalRow: 32,
    pctRow: 33,
    pctLabel: "iii) % of of Job creation in Metropolitan area",
  },
];

const WAGE_LABEL =
  "i) Disclose wages paid to persons employed (including employees or workers employed on a permanent or non-permanent / on contract basis)";

function setColumnWidths(ws) {
  const widths = {
    A: 1.26953125,
    B: 1.26953125,
    C: 9.1796875,
    D: 159.54296875,
    E: 27.1796875,
    F: 27.54296875,
    G: 9.1796875,
    H: 9.1796875,
    I: 9.1796875,
    J: 1.26953125,
  };
  Object.entries(widths).forEach(([col, width]) => {
    ws.getColumn(col).width = width;
  });
  ["A", "B"].forEach((col) => {
    ws.getColumn(col).hidden = true;
  });
  for (let col = 10; col <= 16384; col += 1) {
    ws.getColumn(col).hidden = true;
  }
}

function setupSheetView(workbook, ws) {
  ws.views = [
    {
      showGridLines: false,
      state: "frozen",
      xSplit: 0,
      ySplit: 8,
      topLeftCell: "A9",
      zoomScale: 60,
      zoomScaleNormal: 80,
    },
  ];
  if (!workbook.views?.length) {
    workbook.views = [{ showGridLines: false }];
  } else {
    workbook.views[0].showGridLines = false;
  }
}

function writeBand(ws, row, endCol, text, specs, options = {}) {
  for (let col = COL_D; col <= endCol; col += 1) {
    setCell(ws, row, col, col === COL_D ? text : "", {
      font: options.font,
      bold: options.bold,
      align: options.align || "left",
      fill: options.fill,
      bordered: false,
      border: borderFromSpecs(specs, col),
    });
  }
  if (endCol > COL_D) {
    try {
      ws.mergeCells(row, COL_D, row, endCol);
    } catch {
      /* already merged */
    }
    const startBorder = parseBorderSpec(specs[0]);
    const endBorder = parseBorderSpec(specs[specs.length - 1]);
    ws.getCell(row, COL_D).border = {
      top: startBorder.top || endBorder.top,
      left: startBorder.left,
      bottom: startBorder.bottom || endBorder.bottom,
      right: endBorder.right || startBorder.right,
    };
    if (options.fill) ws.getCell(row, COL_D).fill = options.fill;
    if (options.font) ws.getCell(row, COL_D).font = options.font;
  }
  if (options.height != null) ws.getRow(row).height = options.height;
}

function addDetailLinkRow(ws, row, label, sheetName, options = {}) {
  setCell(ws, row, COL_D, label, {
    align: "left",
    bordered: false,
    border: parseBorderSpec(options.dBorder || "tmtt"),
  });
  setCell(ws, row, COL_E, options.linkText || "Add Details", {
    align: "center",
    link: internalSheetLink(sheetName, DETAIL_CELL),
    bordered: false,
    border: parseBorderSpec(options.eBorder || "tttt"),
  });
  setGrey(ws, row, COL_F, parseBorderSpec(options.fBorder || "---m"));
  if (options.height != null) ws.getRow(row).height = options.height;
  else ws.getRow(row).height = 20.15;
}

function addLabelValueRow(ws, row, label, value, options = {}) {
  setCell(ws, row, COL_D, label, {
    align: "left",
    bordered: false,
    border: parseBorderSpec(options.dBorder || "tmtt"),
  });
  setCell(ws, row, COL_E, value ?? "", {
    align: options.valueAlign || "center",
    fill: options.valueFill === undefined ? VALUE_FILL : options.valueFill,
    bordered: false,
    border: parseBorderSpec(options.eBorder || "ttt-"),
  });
  setGrey(ws, row, COL_F, parseBorderSpec(options.fBorder || "---m"));
  if (options.height != null) ws.getRow(row).height = options.height;
}

function fmtPct(value) {
  if (value === null || value === undefined || value === "") return "";
  const str = String(value).trim();
  if (!str) return "";
  const num = Number(str.replace(/%/g, ""));
  if (!Number.isNaN(num)) return String(num);
  return str;
}

function procurementYesNo(action) {
  if (action === true || action === "true" || action === "yes" || action === "Yes") {
    return "Yes";
  }
  if (action === false || action === "false" || action === "no" || action === "No") {
    return "No";
  }
  if (action === "NA" || action === "N.A." || action === "na") return "NA";
  return safeText(action, "");
}

function writeJobLocationBlock(ws, loc, source) {
  const values = readLocationFY(source?.[loc.key]);
  const pctCurr = fmtPct(values.current);
  const pctPrev = fmtPct(values.previous);

  // Location band — D label, E:F linked grey merge
  setCell(ws, loc.bandRow, COL_D, loc.band, {
    bold: true,
    align: "left",
    bordered: false,
    border: parseBorderSpec("tmtt"),
  });
  setCell(ws, loc.bandRow, COL_E, "", {
    fill: LINKED_FILL,
    bordered: false,
    border: parseBorderSpec("ttt-"),
  });
  setCell(ws, loc.bandRow, COL_F, "", {
    fill: LINKED_FILL,
    bordered: false,
    border: parseBorderSpec("t-tm"),
  });
  try {
    ws.mergeCells(loc.bandRow, COL_E, loc.bandRow, COL_F);
  } catch {
    /* ignore */
  }

  // i) wages — white inputs (app stores % only; leave wages blank)
  setCell(ws, loc.wageRow, COL_D, WAGE_LABEL, {
    align: "left",
    bordered: false,
    border: parseBorderSpec("tmtt"),
  });
  setCell(ws, loc.wageRow, COL_E, "", {
    align: "center",
    fill: VALUE_FILL,
    bordered: false,
    border: parseBorderSpec("tttt"),
  });
  setCell(ws, loc.wageRow, COL_F, "", {
    align: "center",
    fill: VALUE_FILL,
    bordered: false,
    border: parseBorderSpec("t-tm"),
  });

  // ii) total wage cost
  setCell(ws, loc.totalRow, COL_D, "ii) Total Wage Cost", {
    align: "left",
    bordered: false,
    border: parseBorderSpec("tmtt"),
  });
  setCell(ws, loc.totalRow, COL_E, "", {
    align: "center",
    fill: VALUE_FILL,
    bordered: false,
    border: parseBorderSpec("-ttt"),
  });
  setCell(ws, loc.totalRow, COL_F, "", {
    align: "center",
    fill: VALUE_FILL,
    bordered: false,
    border: parseBorderSpec("--tm"),
  });

  // iii) % — use stored percentage (form field); grey like formula cells
  setCell(ws, loc.pctRow, COL_D, loc.pctLabel, {
    align: "left",
    bordered: false,
    border: parseBorderSpec("tmtt"),
  });
  setCell(ws, loc.pctRow, COL_E, pctCurr, {
    align: "center",
    fill: LINKED_FILL,
    bordered: false,
    border: parseBorderSpec("tttt"),
  });
  setCell(ws, loc.pctRow, COL_F, pctPrev, {
    align: "center",
    fill: LINKED_FILL,
    bordered: false,
    border: parseBorderSpec("tttt"),
  });

  [loc.bandRow, loc.wageRow, loc.totalRow, loc.pctRow].forEach((r) => {
    ws.getRow(r).height = 20.15;
  });
}

export function buildPrinciple8Sheet(workbook, sectionC, reportingYear = "") {
  const ws = workbook.addWorksheet("PRINCIPLE 8");
  setColumnWidths(ws);
  setupSheetView(workbook, ws);
  hideRowsThrough(ws, 5);

  const principle = sectionC?.principle8 || {};
  const essential = principle?.essentialIndicators || {};
  const leadership = principle?.leadershipIndicator || {};
  const procurement = leadership?.procurementPolicy || {};
  const { current: fyCurrent, previous: fyPrevious } = getFyLabels(reportingYear);
  const suppliers = essential?.sourceFromSupplier || {};
  const jobCreation = essential?.jobCreationInSmallerTowns || {};
  const procurementAnswer = procurementYesNo(procurement?.action);

  // R8 title
  writeBand(ws, 8, COL_F, PRINCIPLE_TITLE, ["mmt-", "m-t-", "m-tm"], {
    font: TITLE_FONT,
    fill: HEADER_FILL,
    align: "left",
    height: 30,
  });

  // R9 Essential
  writeBand(ws, 9, COL_F, "Essential Indicators", ["tmtt", "tttt", "tttm"], {
    font: SECTION_FONT,
    fill: NA_FILL,
    align: "center",
    height: 20.15,
  });

  // R10–11 detail links
  addDetailLinkRow(
    ws,
    10,
    "1. Details of Social Impact Assessments (SIA) of projects undertaken by the entity based on applicable laws, in the current financial year.",
    DETAILS_OF_SIA_SHEET,
    { dBorder: "-mtt", eBorder: "-ttt", fBorder: "---m" }
  );
  addDetailLinkRow(
    ws,
    11,
    "2. Provide information on project(s) for which ongoing Rehabilitation and Resettlement (R&R) is being undertaken by your entity, in the following format:",
    PROJECT_FOR_REHABILITATION_SHEET
  );

  // R12 community grievances
  addLabelValueRow(
    ws,
    12,
    "3. Describe the mechanisms to receive and redress grievances of the community.",
    safeText(essential?.communityGrievanceMechanisms, ""),
    { eBorder: "tt-t", height: 30, valueAlign: "left" }
  );

  // R13–15 supplier %
  setCell(ws, 13, COL_D, "4. Percentage of input material (inputs to total inputs by value) sourced from suppliers:", {
    align: "left",
    bordered: false,
    border: parseBorderSpec("tmtt"),
  });
  setCell(ws, 13, COL_E, fyCurrent, {
    bold: true,
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec("tttt"),
  });
  setCell(ws, 13, COL_F, fyPrevious, {
    bold: true,
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec("tttm"),
  });
  ws.getRow(13).height = 20.15;

  SUPPLIER_ROWS.forEach(([label, key], idx) => {
    const row = 14 + idx;
    const values = readLocationFY(suppliers[key]);
    const isLast = idx === SUPPLIER_ROWS.length - 1;
    setCell(ws, row, COL_D, label, {
      align: "left",
      bordered: false,
      border: parseBorderSpec("tmtt"),
    });
    setCell(ws, row, COL_E, fmtPct(values.current), {
      align: "center",
      fill: VALUE_FILL,
      bordered: false,
      border: parseBorderSpec(isLast ? "ttt-" : "tt-t"),
    });
    setCell(ws, row, COL_F, fmtPct(values.previous), {
      align: "center",
      fill: VALUE_FILL,
      bordered: false,
      border: parseBorderSpec(isLast ? "tttm" : "t--m"),
    });
    ws.getRow(row).height = 20.15;
  });

  // R16 job creation intro
  writeBand(
    ws,
    16,
    COL_F,
    "5. Job creation in smaller towns - Disclose wages paid to persons employed (including employees or workers employed on a permanent or non-permanent / on contract basis) in the following locations, as % of total wage cost:",
    ["tmtt", "tttt", "tttm"],
    { height: 20.15 }
  );

  // R17 FY headers
  setCell(ws, 17, COL_D, "", {
    bordered: false,
    border: parseBorderSpec("tmtt"),
  });
  setCell(ws, 17, COL_E, fyCurrent, {
    bold: true,
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec("tttt"),
  });
  setCell(ws, 17, COL_F, fyPrevious, {
    bold: true,
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec("tttm"),
  });
  ws.getRow(17).height = 20.15;

  JOB_LOCATIONS.forEach((loc) => writeJobLocationBlock(ws, loc, jobCreation));

  // R34 Leadership
  writeBand(ws, 34, COL_F, "Leadership Indicators", ["tm--", "t---", "t--m"], {
    font: SECTION_FONT,
    fill: NA_FILL,
    align: "left",
    height: 20.15,
  });

  addDetailLinkRow(
    ws,
    35,
    "1. Provide details of actions taken to mitigate any negative social impacts identified in the Social Impact Assessments (Reference: Question 1 of Essential Indicators above):",
    ACTION_TO_MITIGATE_NEGATIVE_SIA_SHEET,
    { eBorder: "ttt-", fBorder: "tt-m", height: 30 }
  );
  addDetailLinkRow(
    ws,
    36,
    "2. Provide the following information on CSR projects undertaken by your entity in designated aspirational districts as identified by government bodies:",
    CSR_IN_ASPIRATIONAL_DISTRICTS_SHEET,
    { eBorder: "ttt-", fBorder: "-t-m", height: 30 }
  );

  addLabelValueRow(
    ws,
    37,
    "3. (a) Do you have a preferential procurement policy where you give preference to purchase from suppliers comprising marginalized /vulnerable groups? (Yes/No/NA)",
    procurementAnswer,
    { eBorder: "ttt-", fBorder: "tttm", height: 30, valueFill: VALUE_FILL }
  );
  // F37 is D9D9D9 in ref
  setGrey(ws, 37, COL_F, parseBorderSpec("tttm"));

  addLabelValueRow(
    ws,
    38,
    "(b) From which marginalized /vulnerable groups do you procure?",
    procurementAnswer === "Yes" ? safeText(procurement?.vulnerableGoupProcure, "") : "",
    {
      eBorder: "ttt-",
      fBorder: "-t-m",
      valueFill: procurementAnswer === "Yes" ? VALUE_FILL : VALUE_FILL,
      valueAlign: "left",
    }
  );

  addLabelValueRow(
    ws,
    39,
    "(c) What percentage of total procurement (by value) does it constitute?",
    procurementAnswer === "Yes" ? fmtPct(procurement?.totalProcurementByValue) : "",
    { eBorder: "ttt-", fBorder: "-t-m", height: 15 }
  );

  addDetailLinkRow(
    ws,
    40,
    "4. Details of the benefits derived and shared from the intellectual properties owned or acquired by your entity (in the current financial year), based on traditional knowledge:",
    DETAILS_OF_BENEFITS_FROM_IP_SHEET,
    { eBorder: "ttt-", fBorder: "-t-m", height: 30 }
  );
  addDetailLinkRow(
    ws,
    41,
    "5. Details of corrective actions taken or underway, based on any adverse order in intellectual property related disputes wherein usage of traditional knowledge is involved.",
    DETAILS_OF_CORRECTIVE_ACTION_IP_SHEET,
    { eBorder: "ttt-", fBorder: "-t-m", height: 30 }
  );
  addDetailLinkRow(
    ws,
    42,
    "6. Details of beneficiaries of CSR Projects:",
    DETAILS_OF_CSR_PROJECT_SHEET,
    { eBorder: "ttt-", fBorder: "-t-m", height: 30 }
  );

  // R43 Notes
  setCell(ws, 43, COL_D, "Notes", {
    align: "left",
    bordered: false,
    border: parseBorderSpec("tmmt"),
  });
  setCell(ws, 43, COL_E, safeText(principle?.notes || leadership?.notes || essential?.notes, ""), {
    align: "left",
    bordered: false,
    border: parseBorderSpec("ttmt"),
  });
  setGrey(ws, 43, COL_F, parseBorderSpec("-tmm"));
  ws.getRow(43).height = 30;

  return ws;
}
